import axiosInstance from '../utils/axiosInstance';

const get = (url, params) => axiosInstance.get(url, { params }).then((r) => r.data);
const post = (url, body) => axiosInstance.post(url, body).then((r) => r.data);

export const api = {
  // receipts shared by every device of the login (lib/sync.js)
  progress: (poIds) => get('api/receiving/progress/', { po: poIds.join(',') }),
  pushProgress: (items) => post('api/receiving/progress/', { items }),
  recentReceipts: () => get('api/receiving/recent/'),
  rememberReceipt: (body) => post('api/receiving/recent/', body),
  orders: (refresh = false) => get('api/receiving/orders/', refresh ? { refresh: 1 } : undefined),
  order: (id) => get(`api/receiving/orders/${id}/`),
  destinations: (id, refresh = false, withIds = []) =>
    get(`api/receiving/orders/${id}/destinations/`, { ...(refresh ? { refresh: 1 } : {}), ...(withIds.length ? { with: withIds.join(',') } : {}) }),
  lookup: (barcode) => get('api/receiving/lookup/', { barcode }),
  linkBarcode: (product_id, barcode, box_id = null, description = '', replace = false) => post('api/receiving/barcodes/link/', { product_id, barcode, box_id, description, replace }),
  // Odoo validating a big receipt is slow, but the sheet can't wait for ever
  confirm: (id, body) => axiosInstance.post(`api/receiving/orders/${id}/confirm/`, body, { timeout: 240000 }).then((r) => r.data),
  createBox: (body) => post('api/receiving/packaging/', body),
  updateBox: (id, body) => axiosInstance.put(`api/receiving/packaging/${id}/`, body).then((r) => r.data),
  deleteBox: (id) => axiosInstance.delete(`api/receiving/packaging/${id}/`).then((r) => r.data),
  setFlags: (productId, flags) => post(`api/receiving/products/${productId}/flags/`, flags),
  receipts: (params = {}) => get('api/receiving/receipts/', params),
  // A ZIP of PDF reports: { ids } or { filters }. Returns the file and the name the server gave it.
  exportReceipts: (body) =>
    axiosInstance.post('api/receiving/receipts/export/', body, { responseType: 'blob', timeout: 180000 }).then((r) => {
      const cd = r.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      return { blob: r.data, filename: m ? m[1] : null };
    }),
  // A failed blob request carries its JSON error inside the blob.
  blobErrorMessage: async (err) => {
    try {
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) return parsed.error;
      }
    } catch {
      /* not JSON */
    }
    return errorMessage(err);
  },
  printLabels: (receiptId) => post(`api/receiving/receipts/${receiptId}/labels/`, {}),
  // newest first; q finds a note by order number, supplier or remark, before pages back
  deliveryNotes: ({ q = '', before = null, limit = 20 } = {}) =>
    get('api/receiving/delivery-notes/', { limit, ...(q ? { q } : {}), ...(before ? { before } : {}) }),
  // what Claude read off the note, matched to today's order lines; post reads it again
  deliveryNoteLines: (id) => get(`api/receiving/delivery-notes/${id}/lines/`),
  rereadDeliveryNote: (id) => post(`api/receiving/delivery-notes/${id}/lines/`, {}),
  // multipart: photos (one per page), po_ids, note; the server merges, attaches in Odoo and logs
  // the note with the open orders found on it that were not picked (after it is read)
  deliveryNote: (id) => get(`api/receiving/delivery-notes/${id}/orders/`),
  labelPrinters: () => get('api/receiving/label-printers/'),
  setLabelPrinter: (printer) => post('api/receiving/label-printers/', { printer }),
  addNoteOrders: (id, poIds, labels) => post(`api/receiving/delivery-notes/${id}/orders/`, { add: poIds, labels }),
  dismissNoteOrders: (id, poIds) => post(`api/receiving/delivery-notes/${id}/orders/`, { dismiss: poIds }),
  createDeliveryNote: (form) => axiosInstance.post('api/receiving/delivery-notes/', form, { timeout: 300000 }).then((r) => r.data),
  // claims: parcels without a PO; Claude reads the ref, the photos go to #sav-warehouse
  claims: (limit = 10) => get('api/receiving/claims/', { limit }),
  claim: (id) => get(`api/receiving/claims/${id}/`),
  createClaim: (form) => axiosInstance.post('api/receiving/claims/', form, { timeout: 120000 }).then((r) => r.data),
  retryClaim: (id) => post(`api/receiving/claims/${id}/`, {}),
  // the selfie wall: posted photo booth pictures, for the whole team
  selfies: (limit = 60) => get('api/receiving/selfies/', { limit }),
  postSelfie: (form) => axiosInstance.post('api/receiving/selfies/', form, { timeout: 60000 }).then((r) => r.data),
  deleteSelfie: (id) => axiosInstance.delete(`api/receiving/selfies/${id}/`).then((r) => r.data),
  adminUsage: (days = 30) => get('api/receiving/admin/usage/', { days }),
};

export function errorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (err?.response?.status === 401) return 'Signed out. Sign in again.';
  if (err?.code === 'ERR_NETWORK') return 'No connection to the server';
  if (err?.code === 'ECONNABORTED') return 'No answer from the server in time. It may still have gone through: try again, Inny checks first.';
  return err?.message || fallback;
}
